import { makeAutoObservable, runInAction } from 'mobx';
import { EditableObject } from 'store/cores/EditableObject';
import { excelParse } from 'services/controllers/o2oReport';
import { EditableArray } from 'store/cores/EditableArray';
import { create, getBoard, sendLinkToMails, update } from 'services/controllers/customBoard';
import isWithinInterval from 'date-fns/isWithinInterval';
import { differenceInCalendarDays } from 'date-fns';
import SETTINGS from 'config/o2o';
import { DatesHelper } from 'utils/datesHelper';
import { FootfallModel } from 'models/Boards/FootfallModel';
import { getFootfallStatistic } from 'services/controllers/footfallReport';

export class FootfallStore {
  settings = new EditableObject();
  tempSettings = new EditableObject();
  pointsPreloader = false;
  reportPreloader = false;
  report = null;
  selectedTileName = '';
  selectedTileId = 'client';
  selectedTileField = null;
  displayTopPoints = [20, 50, 100];
  activeTop = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, null, {
      autoBind: true,
    });
  }

  get getTilesStatistics() {
    const statistic = (this.report?.tiles?.[0]?.coordinateStatistic || []).map((row) => ({
      ...row,
      visitors: row.visits,
    }));

    const summary = { offline: 0 };
    statistic.forEach((value) => {
      summary.offline += value.visitors;
    });

    let sortedStatistic = statistic;

    if (this.activeTop) {
      sortedStatistic = statistic
        .slice()
        .sort((a, b) => b.visitors - a.visitors)
        .slice(0, this.activeTop);
    }

    return [
      {
        id: 'client',
        title: 'Client Target  Group',
        isFootfall: true,
        statistic: sortedStatistic,
        summary,
      },
    ];
  }

  setActiveTop(condition) {
    this.activeTop = condition || null;
  }

  setPointsPreloader(condition) {
    this.pointsPreloader = condition;
  }

  setReportPreloader(condition) {
    this.reportPreloader = condition;
  }

  setReport(data) {
    this.tempSettings.setData(new FootfallModel(data || {}));
    this.report = data;
  }

  setBoardTimeZoneId(id) {
    this.setTempField({ fieldName: 'timeZoneId', data: id });
  }

  setBoardName(name) {
    this.report.name = name;
  }

  setSelectedTileName(name) {
    this.selectedTileName = name;
  }

  setSelectedTileId({ id, field }) {
    this.selectedTileId = id;
    this.selectedTileField = field;
  }

  setTempField({ fieldName, data }) {
    this.tempSettings.data[fieldName] = data;
  }

  get marksInvalidCount() {
    const { startDate, endDate } = this.tempSettings.data;
    const remarks = this.tempSettings.data.remarks?.getValue || [];
    return remarks.reduce(
      (acc, mark) =>
        acc +
        Number(
          !isWithinInterval(DatesHelper.toStartDay(new Date(mark.date)), {
            start: DatesHelper.toStartDay(new Date(startDate)),
            end: DatesHelper.toEndDay(new Date(endDate)),
          }),
        ),
      0,
    );
  }

  get isFormDisabled() {
    const { startDate } = this.tempSettings.data;
    const interval = differenceInCalendarDays(new Date(), new Date(startDate));
    return interval > SETTINGS.MAX_DAYS_BEFORE_TODAY;
  }

  get isSendDisabled() {
    let disabled = false;

    const {
      campaigns,
      folders,
      clientCoordinates,
      groupOfCoordinates,
      isTimeToVisit,
      startTimeToVisit,
      endTimeToVisit,
      attributionPeriod,
    } = this.tempSettings.data;

    const selectCampaign = campaigns?.length || folders?.length;
    const selectClientCoordinates = clientCoordinates?.coordinates?.length;
    const selectTime = isTimeToVisit ? startTimeToVisit && endTimeToVisit : true;

    const allGroupsValid = groupOfCoordinates?.every((group) => group.isValid);

    if (!(selectCampaign && selectClientCoordinates && allGroupsValid && selectTime && attributionPeriod)) {
      disabled = true;
    }

    return disabled;
  }

  getDataFromExcel = async (excel) => {
    try {
      this.setPointsPreloader(true);

      const response = await excelParse(excel);
      if (response !== 'error') {
        const withoutCommasInCoordinates = response
          .slice(1)
          ?.map((el) => el.map((v, i) => (i < 2 ? v?.replace(/,/, '.') : v)))
          ?.filter((el) => Boolean(el?.[3]));

        runInAction(() => {
          this.tempSettings.setField({
            name: 'excelPoints',
            value: new EditableArray({ data: withoutCommasInCoordinates, id: '' }),
          });
        });
      }
      return response;
    } finally {
      this.setPointsPreloader(false);
    }
  };

  getBoardAsync = async (id) => {
    try {
      this.setReportPreloader(true);
      let report = await getBoard(id);
      report.footfallStatistic = await getFootfallStatistic(id);

      runInAction(() => {
        const model = new FootfallModel(report);
        this.setReport({ ...report });
        this.tempSettings.setData(model);
      });

      return report;
    } catch (e) {
      console.log(e);
    } finally {
      this.setReportPreloader(false);
    }
  };

  sendFootfallBoard = async ({ data, mode }) => {
    try {
      this.rootStore.utilsStore.setError(false);
      this.rootStore.utilsStore.setProgress(true);
      this.rootStore.utilsStore.setPending(true);

      if (mode === 'create') {
        return await create(data);
      } else {
        await update(data);
      }
    } catch (e) {
      this.rootStore.utilsStore.setError(true);
    } finally {
      this.rootStore.utilsStore.setComplete(true);
    }
  };

  sendToMails = async (data) => {
    await sendLinkToMails(data);
    this.rootStore.messageStore.setMessage({
      data: { statusText: 'Successfully sent' },
      type: 'success',
    });
  };
}
